import { FC, SyntheticEvent, useMemo, useState, UIEvent, useEffect } from 'react';
import { debounce, get } from 'lodash';
import { useFormikContext } from 'formik';
import { Box, Stack, Typography } from '@mui/material';

import { ServiceType } from 'domain/entities/ServiceType';
import { Entity } from 'domain/types/Entity';
import { ID } from 'domain/types/ID';
import { useCaseSearchCustomerServiceTypes } from 'application/prices/useCases/useCaseSearchCustomerServiceTypes';
import { useTranslationPrefix } from 'infrastructure/translations/i18n';
import DEFAULT_DEBOUNCE_TIME from 'infrastructure/utils/defaultDebounceTime';
import { Autocomplete, TextField } from 'targets/web/components';

const LISTBOX_SCROLL_THRESHOLD = 20;

const ServiceTypeAutocomplete: FC<{
  optional?: boolean;
}> = ({ optional }) => {
  const t = useTranslationPrefix('components.service_type_autocomplete');
  const [serviceTypePhrase, setServiceTypePhrase] = useState('');
  const [page, setPage] = useState(0);
  const [fetchedServiceTypes, setFetchedServiceTypes] = useState<Entity<ServiceType>[]>([]);
  const [hasMore, setHasMore] = useState(true);

  const { values, setFieldValue, errors, touched } = useFormikContext<{
    customer?: { id: ID };
    serviceTypes: Entity<ServiceType>[];
  }>();

  const {
    items = [],
    isLoading,
    isFetching,
    changePage,
    error,
  } = useCaseSearchCustomerServiceTypes({
    phrase: serviceTypePhrase.length >= 2 ? serviceTypePhrase : undefined,
    customerId: values.customer?.id,
    limit: 200,
  });

  useEffect(() => {
    if (values.customer?.id) {
      setFieldValue('serviceTypes', []);
      changePage(0);
      setFetchedServiceTypes([]);
    }
  }, [values.customer?.id, changePage, setFieldValue, setFetchedServiceTypes]);

  useEffect(() => {
    if (isLoading || isFetching || error) {
      return;
    }

    if (page === 0) {
      setFetchedServiceTypes(items);
      return;
    }

    setFetchedServiceTypes((prevServiceTypes) => [
      ...prevServiceTypes,
      ...items.filter((nc) => !prevServiceTypes.some((pc) => pc.id === nc.id)),
    ]);
  }, [items, page, isLoading, isFetching, error]);

  const handleInputChange = useMemo(
    () =>
      debounce((event: SyntheticEvent, newInputValue: string) => {
        if (event?.type !== 'change' && newInputValue) {
          return;
        }

        setServiceTypePhrase(newInputValue);
      }, DEFAULT_DEBOUNCE_TIME),
    [],
  );

  const handleScroll = useMemo(
    () =>
      debounce((event: UIEvent<HTMLUListElement>) => {
        const listNode = event.target as EventTarget & HTMLUListElement;

        if (
          listNode.scrollTop + listNode.clientHeight >=
            listNode.scrollHeight - LISTBOX_SCROLL_THRESHOLD &&
          !isLoading &&
          hasMore
        ) {
          setPage((prevPage) => prevPage + 1);
        }
      }, DEFAULT_DEBOUNCE_TIME),
    [hasMore, isLoading],
  );

  useEffect(() => {
    changePage(page);
  }, [page, changePage]);

  useEffect(() => {
    setHasMore(items.length > 0);
  }, [items]);

  useEffect(() => {
    setPage(0);
  }, [serviceTypePhrase, setPage]);

  return (
    <Autocomplete<Entity<Pick<ServiceType, 'name' | 'code'>>, true, false, boolean>
      label={t('label')}
      multiple
      loading={isLoading}
      disabled={!values.customer?.id}
      placeholder={t('placeholder')}
      options={fetchedServiceTypes}
      value={values.serviceTypes}
      noOptionsText={
        <Stack alignItems="start" sx={{ marginInline: -4, marginBlock: -2 }}>
          <Box p={3}>
            <Typography color="text.secondary" variant="menuItem">
              {error ? t('query_error') : t('no_options')}
            </Typography>
          </Box>
        </Stack>
      }
      ListboxProps={{ onScroll: handleScroll }}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      filterOptions={(x) => x}
      onInputChange={handleInputChange}
      onChange={(_, value) => {
        setFieldValue('serviceTypes', value);
      }}
      renderInput={(params) => {
        const hasError = !!get(errors, 'serviceTypes') && !!get(touched, 'serviceTypes');

        return (
          <TextField
            {...params}
            name="serviceTypes"
            label={t('label')}
            inputProps={{
              ...params.inputProps,
              required: !optional,
            }}
            error={hasError}
            helperText={hasError ? t('error') : undefined}
            required={!optional}
          />
        );
      }}
    />
  );
};

export default ServiceTypeAutocomplete;
